import React, { useEffect, useRef, useState } from 'react';
import { MAGNITT_ACCESS_ROLES } from '/src/lib/constants/userRoles';
import investorApi from '/src/apis/investorApi';
import { getStartupStatusBadge } from '/src/components/ICESections/Shared/helpers';
import AppMagnittLoader from '/src/components/Reusable/AppMagnittLoader';
// TODO [feat-86exwhkgb]: Card view removed — restore this import if card view is re-enabled
// import FundPortfolioCard from './Components/FundPortfolioCard/FundPortfolioCard';
import FundPortfolioTableBody, { getPortfolioCompanyUrl } from './Components/FundPortfolioTableBody/FundPortfolioTableBody';
import FundPortfolioTabs from './Components/FundPortfolioTabs/FundPortfolioTabs';
import Pagination from '/src/components/Pagination/Pagination';
import { getInitials, MOB_BADGE_COLORS, parseWebsiteUrl } from '/src/components/ICESections/Shared/mobileListUtils';
import { SectionEmptyState, SectionErrorState } from '/src/components/ICESections/Shared/SectionEmptyState';
import { BALANCE_SHEET_FUND_TYPE, BALANCE_SHEET_LABEL } from '../FundsRaised/constants';

const DEFAULT_LIMIT = 10;

const abbrevMobile = (text) => {
    if (!text) return text;
    if (/m&a|mergers?\s*(?:and|&)\s*acquisitions?/i.test(text)) return 'M&A';
    return text;
};

// portfolioCompanyCount: the profile's number_of_entities, the companies the investor backed directly (the pre-compute's
// number_of_companies, startups and corporates), shown as the section subtitle
const FundPortfolio = ({ entityId, entityType, investorName, portfolioCompanyCount, user }) => {
    const [funds, setFunds] = useState(null);
    const [tabData, setTabData] = useState([]);
    const [tabTotal, setTabTotal] = useState(0);
    const [currentTab, setCurrentTab] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(DEFAULT_LIMIT);
    // TODO [feat-86exwhkgb]: Card view removed — restore these states if card/table toggle is re-enabled
    // const [cardView, setCardView] = useState(false);
    // const [tableView, setTableView] = useState(true);
    // Loading until the first page arrives, so the empty-fund message never shows before it
    const [isLoading, setIsLoading] = useState(true);
    const [isPaginationLoading, setIsPaginationLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [hasTabError, setHasTabError] = useState(false);
    const [expandedMobileRows, setExpandedMobileRows] = useState([]);
    const [scrollPos, setScrollPos] = useState(0);
    const [maxScroll, setMaxScroll] = useState(null);
    const [stickyColWidth, setStickyColWidth] = useState(200);
    const tableScrollRef = useRef(null);
    const fundsRef = useRef(null);
    const latestRequestId = useRef(0);
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        const el = tableScrollRef.current;
        if (!el) return;
        const update = () => {
            setScrollPos(el.scrollLeft);
            setMaxScroll(el.scrollWidth - el.clientWidth);
            const firstTh = el.querySelector('th:first-child');
            if (firstTh) setStickyColWidth(firstTh.offsetWidth);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [tabData, currentTab]);

    const handleTableScroll = () => {
        const el = tableScrollRef.current;
        if (!el) return;
        setScrollPos(el.scrollLeft);
        setMaxScroll(el.scrollWidth - el.clientWidth);
    };

    const scrollTable = (direction) => {
        const el = tableScrollRef.current;
        if (!el) return;
        const amount = Math.max(el.clientWidth - stickyColWidth, 150);
        el.scrollLeft += direction === 'left' ? -amount : amount;
    };

    useEffect(() => {
        if (user.is_loggedin && user.roles.includes(MAGNITT_ACCESS_ROLES.directory_access)) {
            fetchFundList();
        }
    }, []);

    // Re-fetch when tab, page, or limit changes — isInitialized gates the first run
    useEffect(() => {
        if (!isInitialized) return;
        fetchTabPage(currentTab, page, limit);
    }, [isInitialized, currentTab, page, limit]);

    const fetchFundList = async () => {
        try {
            const [resPortfolio, resOthers] = await Promise.all([
                investorApi.getFundPortfolio(entityId, entityType),
                investorApi.getStartupsInvestedByFund({ fundId: 'others', entityId, entityType, limit: 1, page: 1 }).catch(() => null),
            ]);
            // the fund with the most companies first, so the section opens on it and funds with none sit at the end
            // (the API returns newest first, and the sort keeps that order among equal counts)
            // balance sheet tab reads "Balance Sheet" whatever its stored name, as in the Managed Funds table
            const fundsByCompanies = (resPortfolio || []).map((fund) => (
                Number(fund.is_balance_sheet) === BALANCE_SHEET_FUND_TYPE ? { ...fund, name: BALANCE_SHEET_LABEL } : fund
            )).sort((a, b) =>
                Number(b.companies_investedin_count || 0) - Number(a.companies_investedin_count || 0));
            const fundList = [
                ...fundsByCompanies,
                { id: 'others', name: 'Others', companies_investedin_count: resOthers?.totalCount || 0 },
            ];
            fundsRef.current = fundList;
            setFunds(fundList);
            setIsInitialized(true);
        } catch (e) {
            setFunds([]);
            setHasError(true);
        }
    };

    const fetchTabPage = async (tabIndex, currentPage, currentLimit) => {
        const fundList = fundsRef.current;
        if (!fundList?.[tabIndex]) return;
        const requestId = ++latestRequestId.current;
        setIsLoading(true);
        try {
            const fund = fundList[tabIndex];
            const { data, totalCount } = await investorApi.getStartupsInvestedByFund({
                fundId: fund.id,
                entityId,
                entityType,
                limit: currentLimit,
                page: currentPage,
            });
            if (requestId !== latestRequestId.current) return;
            setTabData(data || []);
            setTabTotal(totalCount);
            setHasTabError(false);
        } catch (e) {
            if (requestId !== latestRequestId.current) return;
            console.error('Failed to load the fund portfolio startups', e);
            setTabData([]);
            setHasTabError(true);
        } finally {
            if (requestId === latestRequestId.current) {
                setIsLoading(false);
                setIsPaginationLoading(false);
            }
        }
    };

    const updatePaginationState = (newState) => {
        if (newState.limit !== undefined) setLimit(newState.limit);
        if (newState.isLoading !== undefined) setIsPaginationLoading(newState.isLoading);
        // limit change always resets to page 1; plain page change uses the given value
        const nextPage = newState.limit !== undefined ? 1 : newState.page;
        if (nextPage !== undefined) setPage(nextPage);
    };

    const handleChangeTab = (index) => {
        setCurrentTab(index);
        setPage(1);
        setExpandedMobileRows([]);
    };

    // TODO [feat-86exwhkgb]: Card view removed — restore this handler if card/table toggle is re-enabled
    // const changeViewTab = (view) => {
    //     setCardView(view === 'card');
    //     setTableView(view === 'table');
    // };

    const toggleMobileRow = (id) => setExpandedMobileRows(prev =>
        prev.includes(id) ? prev.filter(k => k !== id) : [...prev, id]
    );

    if (!funds) return null;

    if (hasError && funds.length === 0) {
        return (
            <div className="portfolio-card">
                <div className="fund-portfolio-section">
                    <h2 className="profile-section-secondary-heading">Fund Portfolio</h2>
                    <SectionErrorState />
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="portfolio-card">
                <div className="fund-portfolio-section">
                    <div className="profile-header-section">
                        <div className="fp-header-left">
                            <h2 className="profile-section-secondary-heading">Fund Portfolio</h2>
                            {/* The investor's direct-investment companies: it stays the same whichever fund pill is selected */}
                            {portfolioCompanyCount != null && (
                                <div className="title">
                                    <span className="investor-name">{investorName}</span> has
                                    <span className="portfolio-count"> {Number(portfolioCompanyCount)}</span>
                                    {' '}compan{Number(portfolioCompanyCount) === 1 ? 'y' : 'ies'} in its portfolio
                                </div>
                            )}
                        </div>
                        {/* TODO [feat-86exwhkgb]: Card view removed — restore this toggle UI if card/table switching is re-enabled
                        <div className="fp-view-toggle">
                            <button type="button" className={`fp-view-btn ${tableView ? 'fp-view-btn--active' : ''}`} onClick={() => changeViewTab('table')}>
                                <i className="fas fa-table" />
                                Table View
                            </button>
                            <button type="button" className={`fp-view-btn ${cardView ? 'fp-view-btn--active' : ''}`} onClick={() => changeViewTab('card')}>
                                <i className="fa fa-window-maximize" />
                                Card View
                            </button>
                        </div> */}
                    </div>

                    {funds.length > 0 ? (
                        <>
                            <FundPortfolioTabs
                                funds={funds}
                                currentTab={currentTab}
                                onTabChange={handleChangeTab}
                            />

                            {isLoading ? (
                                <AppMagnittLoader data={{ height: '300px', background: '#FFF' }} />
                            ) : (
                                <>
                                    {/* TODO [feat-86exwhkgb]: Card view removed — restore this block (and FundPortfolioCard import/component) if card view is re-enabled
                                    {cardView && tabData.length > 0 && (
                                        <div className="card-container">
                                            <div className="card-details managed-portfolio-card-details">
                                                {tabData.map((startup, i) => (
                                                    <FundPortfolioCard startup={startup} key={startup.id} />
                                                ))}
                                            </div>
                                        </div>
                                    )} */}
                                    {tabData.length === 0 && (hasTabError ? <SectionErrorState /> : (
                                        <SectionEmptyState>
                                            {funds[currentTab]?.id === 'others' ? 'No companies to display outside its funds' : 'No companies to display for this fund'}
                                        </SectionEmptyState>
                                    ))}
                                    {tabData.length > 0 && (
                                        <div className="funds-raised-wrapper fp-portfolio-table">
                                            {/* ── Mobile card list ── */}
                                            <div className="funds-raised-mobile-list">
                                                {tabData.map((startup, idx) => {
                                                    if (!startup) return null;
                                                    const rowId = startup.id ?? idx;
                                                    const isOpen = expandedMobileRows.includes(rowId);
                                                    const col = MOB_BADGE_COLORS[idx % MOB_BADGE_COLORS.length];
                                                    const profileUrl = getPortfolioCompanyUrl(startup);
                                                    const website = parseWebsiteUrl(startup?.website);

                                                    return (
                                                        <div
                                                            key={rowId}
                                                            className="fr-mob-row"
                                                            onClick={() => toggleMobileRow(rowId)}
                                                        >
                                                            <div className="fr-mob-row__top">
                                                                <div className="fr-mob-badge" style={{ background: col.bg, color: col.txt }}>
                                                                    {getInitials(startup.name)}
                                                                </div>
                                                                <div className="fr-mob-row__main">
                                                                    {profileUrl ? (
                                                                        <a
                                                                            href={profileUrl}
                                                                            className="funds-raised-table__fund-link"
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            {startup.name}
                                                                        </a>
                                                                    ) : <span>{startup.name}</span>}
                                                                    <div className="fr-mob-row__meta">
                                                                        {startup.hq_data?.name
                                                                            ? <span>{startup.hq_data.name}</span>
                                                                            : <span className="funds-raised-table__empty">–</span>}
                                                                    </div>
                                                                </div>
                                                                <div className="fr-mob-row__right">
                                                                    {startup.funding_stage_data?.name && (
                                                                        <span className="fr-status-badge fr-status-badge--closed">
                                                                            {abbrevMobile(startup.funding_stage_data.name)}
                                                                        </span>
                                                                    )}
                                                                    <div className="fr-mob-row__status-row">
                                                                        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} fr-mob-row__chevron`} />
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {isOpen && (
                                                                <div className="fr-mob-row__expand">
                                                                    <div className="mob-expand-grid">
                                                                        <div className="mob-expand-cell">
                                                                            <div className="mob-expand-cell__lbl">Primary Industry</div>
                                                                            <div className="mob-expand-cell__val">
                                                                                {startup.primary_industry_data?.name
                                                                                    ? abbrevMobile(startup.primary_industry_data.name)
                                                                                    : <span className="funds-raised-table__empty">–</span>}
                                                                            </div>
                                                                        </div>
                                                                        <div className="mob-expand-cell">
                                                                            <div className="mob-expand-cell__lbl">Status</div>
                                                                            <div className="mob-expand-cell__val">
                                                                                {getStartupStatusBadge(startup)}
                                                                            </div>
                                                                        </div>
                                                                        {website && (
                                                                            <div className="mob-expand-cell">
                                                                                <div className="mob-expand-cell__lbl">Website</div>
                                                                                <div className="mob-expand-cell__val">
                                                                                    <a
                                                                                        className="funds-raised-table__fund-link fp-website-link"
                                                                                        href={website.href}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                    >
                                                                                        <i className="fa fa-link" />
                                                                                        {website.display}
                                                                                    </a>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            <div className="funds-raised-scroll-area">
                                                {scrollPos > 0 && (
                                                    <button
                                                        className="funds-raised-scroll-btn"
                                                        style={{ left: stickyColWidth }}
                                                        onClick={() => scrollTable('left')}
                                                        aria-label="Scroll left"
                                                    >
                                                        <i className="fas fa-chevron-left" />
                                                    </button>
                                                )}
                                                {maxScroll > 0 && scrollPos < maxScroll && (
                                                    <button
                                                        className="funds-raised-scroll-btn funds-raised-scroll-btn--right"
                                                        onClick={() => scrollTable('right')}
                                                        aria-label="Scroll right"
                                                    >
                                                        <i className="fas fa-chevron-right" />
                                                    </button>
                                                )}
                                                <div className="funds-raised-scroll" ref={tableScrollRef} onScroll={handleTableScroll}>
                                                    <table className="funds-raised-table">
                                                        <thead>
                                                            <tr className="funds-raised-table__header">
                                                                <th>Name</th>
                                                                <th>Company HQ</th>
                                                                <th>Primary Industry</th>
                                                                <th>Funding Stage</th>
                                                                <th>Status</th>
                                                                <th>Website</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <FundPortfolioTableBody data={tabData} />
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    ) : (
                        <p>There is not enough information about the company investments made by this {entityType}&apos;s funds.</p>
                    )}
                </div>
            </div>

            {tabTotal > DEFAULT_LIMIT && (
                <div className="funding-table-pagination" data-table-type="managed-funds-section">
                    <Pagination
                        page={page}
                        totalRecords={tabTotal}
                        limit={limit}
                        limitOptions={[10, 20, 30]}
                        setParentState={updatePaginationState}
                        fetchData={() => {
                            /* fetch driven by [isInitialized, currentTab, page, limit] effect — wiring fetchData here would cause a double-fetch */
                        }}
                        isLoading={isPaginationLoading}
                        tableName="fund-portfolio-tabs"
                    />
                </div>
            )}
        </>
    );
};

export default FundPortfolio;
